import { useEffect, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";

const FLAGS = {
  "Mexico": "🇲🇽", "South Africa": "🇿🇦", "South Korea": "🇰🇷",
  "Czech Republic": "🇨🇿",
  "Canada": "🇨🇦", "Switzerland": "🇨🇭", "Qatar": "🇶🇦",
  "Bosnia & Herzegovina": "🇧🇦",
  "Brazil": "🇧🇷", "Morocco": "🇲🇦", "Scotland": "🏴󠁧󠁢󠁳󠁣󠁴󠁿", "Haiti": "🇭🇹",
  "USA": "🇺🇸", "Paraguay": "🇵🇾", "Australia": "🇦🇺", "Turkey": "🇹🇷",
  "Germany": "🇩🇪", "Ivory Coast": "🇨🇮", "Ecuador": "🇪🇨", "Curaçao": "🇨🇼",
  "Netherlands": "🇳🇱", "Tunisia": "🇹🇳", "Japan": "🇯🇵", "Sweden": "🇸🇪",
  "Belgium": "🇧🇪", "Egypt": "🇪🇬", "Iran": "🇮🇷", "New Zealand": "🇳🇿",
  "Spain": "🇪🇸", "Cape Verde": "🇨🇻", "Saudi Arabia": "🇸🇦", "Uruguay": "🇺🇾",
  "France": "🇫🇷", "Senegal": "🇸🇳", "Norway": "🇳🇴", "Iraq": "🇮🇶",
  "Argentina": "🇦🇷", "Algeria": "🇩🇿", "Austria": "🇦🇹", "Jordan": "🇯🇴",
  "Portugal": "🇵🇹", "Colombia": "🇨🇴", "Uzbekistan": "🇺🇿", "DR Congo": "🇨🇩",
  "England": "🏴󠁧󠁢󠁥󠁮󠁧󠁿", "Croatia": "🇭🇷", "Ghana": "🇬🇭", "Panama": "🇵🇦",
};

function getFlag(team) {
  return FLAGS[team] || "🏳️";
}

function isPredictionOpen(kickoff) {
  const kickoffTime = new Date(kickoff);
  const deadline = new Date(kickoffTime.getTime() - 60 * 60 * 1000);
  return new Date() < deadline;
}

function formatKickoff(kickoff) {
  const date = new Date(kickoff);
  return {
    date: date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }),
    time: date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }),
  };
}

function getDeadline(kickoff) {
  const kickoffTime = new Date(kickoff);
  const deadline = new Date(kickoffTime.getTime() - 60 * 60 * 1000);
  return deadline.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

function groupByDate(matches) {
  const sections = [];
  let currentDate = null;
  matches.forEach((match) => {
    const dateLabel = new Date(match.kickoff).toLocaleDateString("en-GB", {
      weekday: "long", day: "numeric", month: "long"
    });
    if (dateLabel !== currentDate) {
      currentDate = dateLabel;
      sections.push({ dateLabel, matches: [match] });
    } else {
      sections[sections.length - 1].matches.push(match);
    }
  });
  return sections;
}

// Reusable teams row — handles long names and mobile alignment
function TeamsRow({ match, resultText, dimmed }) {
  return (
    <div className="flex items-center gap-2 w-full">
      {/* Home team */}
      <div className="flex-1 min-w-0 text-left">
        <span
          className="font-display tracking-wider leading-tight block"
          style={{
            color: dimmed ? "#9CA3AF" : "#0A0B0F",
            fontSize: "clamp(0.9rem, 3vw, 1.3rem)",
            wordBreak: "break-word",
          }}
        >
          {getFlag(match.homeTeam)} {match.homeTeam}
        </span>
      </div>

      {/* Centre — VS or result */}
      <div className="flex-shrink-0 text-center px-1">
        {resultText ? (
          <span className="font-display text-2xl tracking-wider" style={{ color: "#0A0B0F" }}>
            {resultText}
          </span>
        ) : (
          <span className="font-condensed text-gray-300 text-sm">VS</span>
        )}
      </div>

      {/* Away team */}
      <div className="flex-1 min-w-0 text-right">
        <span
          className="font-display tracking-wider leading-tight block"
          style={{
            color: dimmed ? "#9CA3AF" : "#0A0B0F",
            fontSize: "clamp(0.9rem, 3vw, 1.3rem)",
            wordBreak: "break-word",
          }}
        >
          {match.awayTeam} {getFlag(match.awayTeam)}
        </span>
      </div>
    </div>
  );
}

export default function Events() {
  const { currentUser } = useAuth();
  const [matches, setMatches] = useState([]);
  const [predictions, setPredictions] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      const matchSnap = await getDocs(collection(db, "matches"));
      const matchData = matchSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      matchData.sort((a, b) => new Date(a.kickoff) - new Date(b.kickoff));
      setMatches(matchData);

      const predSnap = await getDocs(
        query(collection(db, "predictions"), where("userId", "==", currentUser.uid))
      );
      const predMap = {};
      predSnap.docs.forEach((doc) => {
        const data = doc.data();
        predMap[data.matchId] = data;
      });
      setPredictions(predMap);
      setLoading(false);
    }
    fetchData();
  }, [currentUser]);

  const sections = groupByDate(matches);

  function MatchCard({ match }) {
    const { time } = formatKickoff(match.kickoff);
    const deadline = getDeadline(match.kickoff);
    const open = isPredictionOpen(match.kickoff);
    const completed = match.status === "completed";
    const prediction = predictions[match.id];
    const hasPrediction = !!prediction;

    // Completed result card
    if (completed) {
      return (
        <div className="card p-4 md:p-6" style={{ opacity: 0.75 }}>
          <div className="flex justify-between items-center mb-3">
            <span className="font-condensed text-gray-400 tracking-widest text-xs uppercase">
              Group {match.group} · {time} BST
            </span>
            <span className="font-condensed text-xs tracking-wider px-3 py-1 rounded-full"
              style={{ backgroundColor: "#F0F0F0", color: "#999" }}>
              FINAL
            </span>
          </div>

          <TeamsRow match={match} resultText={match.result} dimmed />

          {match.firstGoalScorer && (
            <p className="font-condensed text-gray-400 text-sm tracking-wide text-center mt-3">
              ⚽ First scorer: {match.firstGoalScorer}
            </p>
          )}
          {hasPrediction && (
            <div className="mt-3 rounded-xl px-4 py-3"
              style={{ backgroundColor: "#F5F6F2", border: "1px solid #E0E2DB" }}>
              <p className="font-condensed text-xs tracking-widest text-gray-400 uppercase mb-1">Your Prediction</p>
              <p className="font-condensed text-sm tracking-wide" style={{ color: "#0A0B0F" }}>
                {getFlag(prediction.winner !== "Draw" ? prediction.winner : "")} {prediction.winner} · {prediction.homeScore}–{prediction.awayScore}
                {prediction.goalscorer && ` · ⚽ ${prediction.goalscorer}`}
              </p>
              {prediction.pointsEarned > 0 && (
                <p className="font-condensed text-sm mt-1 font-bold" style={{ color: "#3A6600" }}>
                  +{prediction.pointsEarned} pts earned
                </p>
              )}
            </div>
          )}
        </div>
      );
    }

    // Predictions closed — in play
    if (!open) {
      return (
        <div className="card p-4 md:p-6" style={{ borderTop: "3px solid #FF5C00", opacity: 0.85 }}>
          <div className="flex justify-between items-center mb-3">
            <span className="font-condensed text-gray-400 tracking-widest text-xs uppercase">
              Group {match.group} · {time} BST
            </span>
            <span className="font-condensed text-xs tracking-wider px-3 py-1 rounded-full"
              style={{ backgroundColor: "#FFF3E0", color: "#B85C00" }}>
              IN PLAY
            </span>
          </div>

          <div className="mb-3">
            <TeamsRow match={match} dimmed />
          </div>

          {hasPrediction ? (
            <div className="rounded-xl px-4 py-3"
              style={{ backgroundColor: "#F5F6F2", border: "1px solid #E0E2DB" }}>
              <p className="font-condensed text-xs tracking-widest text-gray-400 uppercase mb-1">Your Prediction</p>
              <p className="font-condensed text-sm tracking-wide" style={{ color: "#0A0B0F" }}>
                {prediction.winner} · {prediction.homeScore}–{prediction.awayScore}
                {prediction.goalscorer && ` · ⚽ ${prediction.goalscorer}`}
              </p>
            </div>
          ) : (
            <div className="font-condensed text-center text-gray-400 text-sm tracking-wide py-2">
              Predictions are closed
            </div>
          )}
        </div>
      );
    }

    // Open for predictions
    return (
      <div className="card p-4 md:p-6 hover:shadow-lg transition-all"
        style={{ borderTop: `3px solid ${hasPrediction ? "#3B82F6" : "#C8FF00"}` }}>

        {/* Header row */}
        <div className="flex justify-between items-center mb-3">
          <div>
            <span className="font-condensed text-gray-400 tracking-widest text-xs uppercase block">
              Group {match.group}
            </span>
            <span className="font-display text-xl tracking-wider" style={{ color: "#0A0B0F" }}>
              {time} <span className="font-condensed text-sm text-gray-400 normal-case tracking-wide">BST</span>
            </span>
          </div>
          {hasPrediction ? (
            <span className="font-condensed text-xs tracking-wider px-3 py-1 rounded-full"
              style={{ backgroundColor: "#EFF6FF", color: "#3B82F6" }}>
              ✓ PREDICTED
            </span>
          ) : (
            <span className="font-condensed text-xs tracking-wider px-3 py-1 rounded-full"
              style={{ backgroundColor: "#EDFFD0", color: "#3A6600" }}>
              OPEN
            </span>
          )}
        </div>

        {/* Teams */}
        <div className="mb-2">
          <TeamsRow match={match} />
        </div>

        <p className="font-condensed text-gray-400 text-xs tracking-wide text-center mb-4">
          Predictions close at {deadline} BST
        </p>

        {/* Prediction preview */}
        {hasPrediction && (
          <div className="mb-4 px-4 py-3 rounded-xl"
            style={{ backgroundColor: "#EFF6FF", border: "1px solid #BFDBFE" }}>
            <p className="font-condensed text-xs tracking-widest uppercase mb-1"
              style={{ color: "#3B82F6" }}>Your Prediction</p>
            <p className="font-condensed text-sm tracking-wide" style={{ color: "#1E40AF" }}>
              {prediction.winner} · {prediction.homeScore}–{prediction.awayScore}
              {prediction.goalscorer && ` · ⚽ ${prediction.goalscorer}`}
            </p>
          </div>
        )}

        {/* CTA */}
        {hasPrediction ? (
          <Link to={`/predict/${match.id}`}
            className="w-full py-3 rounded-xl text-center block font-condensed text-base tracking-wide transition-all"
            style={{ backgroundColor: "#EFF6FF", color: "#3B82F6", border: "2px solid #3B82F6" }}>
            EDIT PREDICTION
          </Link>
        ) : (
          <Link to={`/predict/${match.id}`} className="btn-lime w-full py-3 rounded-xl text-center block">
            MAKE PREDICTION
          </Link>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F0F1EC" }}>
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 md:py-10">

        <div className="mb-8 fade-up">
          <p className="font-condensed text-gray-500 tracking-widest uppercase text-sm mb-2">
            Group Stage · June 11 – 27
          </p>
          <h2 className="font-display text-5xl md:text-7xl tracking-wider" style={{ color: "#0A0B0F" }}>
            WORLD CUP 2026
          </h2>
        </div>

        {loading ? (
          <p className="font-condensed text-gray-400 text-xl">Loading fixtures...</p>
        ) : (
          <div className="space-y-10 md:space-y-12 fade-up fade-up-delay-1">
            {sections.map((section) => (
              <div key={section.dateLabel}>
                <div className="flex items-center gap-4 mb-5">
                  <h3
                    className="font-display tracking-wider flex-shrink-0"
                    style={{ color: "#0A0B0F", fontSize: "clamp(1.2rem, 4vw, 1.875rem)" }}
                  >
                    {section.dateLabel.toUpperCase()}
                  </h3>
                  <div className="flex-1 h-px" style={{ backgroundColor: "#E0E2DB" }} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {section.matches.map((match) => (
                    <MatchCard key={match.id} match={match} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
